import type { TemplateCoverImageAlignment, TemplateCoverImageSize } from '@/templates/domain/types/TemplateCoverImage';
import { A4_HEIGHT_PX, A4_WIDTH_PX } from '../domain/constants/pageMetrics';

/**
 * Cover-page image layout, shared by the HTML preview (`CoverImageLayer`) and the react-pdf export
 * (`CoverImageLayerPdf`) so the two draw the same thing — change a value here, never in one renderer.
 *
 * The image is the cover page's own background layer (never the page background — decision Q8):
 * anchored to the top of the cover area, behind the cover text, `size` of the cover width, placed
 * left / centre / right by `alignment`, or tiled edge to edge (each tile `size` wide, at the
 * image's own aspect ratio) when `repeat` is on.
 */
export const COVER_IMAGE_WIDTH_PERCENT: Record<TemplateCoverImageSize, number> = {
  small: 25,
  medium: 50,
  full: 100
};

/** Control defaults (Figma 11:61). Mirrors `DEFAULT_TEMPLATE_COVER_IMAGE`; `documentBranding.coverImage.test.ts` keeps them equal. */
export const COVER_IMAGE_DEFAULT_SIZE: TemplateCoverImageSize = 'small';
export const COVER_IMAGE_DEFAULT_ALIGNMENT: TemplateCoverImageAlignment = 'left';

/** The widest image (width ÷ height) a repeating cover image is sized for; wider ones leave a gap at the page bottom. */
const MAX_REPEAT_ASPECT_RATIO = 4;

/**
 * Number of tiles a repeating cover image needs to fill an A4 cover: the whole columns that fit one
 * row, times the rows that reach the page bottom for the flattest supported image, plus one spare
 * (page padding makes the tiles slightly smaller than the A4 maths). A renderer cannot know the
 * image's aspect ratio up front, so it draws this many and the cover clips the surplus.
 */
export function coverImageTileCount(size: TemplateCoverImageSize): number {
  const tileWidthPx = A4_WIDTH_PX * (COVER_IMAGE_WIDTH_PERCENT[size] / 100);
  const columns = Math.round(100 / COVER_IMAGE_WIDTH_PERCENT[size]);
  const rows = Math.ceil(A4_HEIGHT_PX / (tileWidthPx / MAX_REPEAT_ASPECT_RATIO)) + 1;
  return columns * rows;
}
